import { FormikProvider, useFormik } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import { CampaignCreationSteps } from "../components/campaign/campaign-constants";
import { type CampaignFormValues } from "../components/campaign/campaign-form";
import {
  defaultCampaignFormValues,
  getCampaignBrandId,
  getCloneCampaignFormValues,
} from "../components/campaign/campaign-form-values";
import { CampaignFooter } from "../components/campaign/layout/campaign-footer";
import { ProgressSidebar } from "../components/campaign/layout/progress-sidebar";
import { StepRenderer } from "../components/campaign/layout/step-renderer";
import { useCreateCampaign } from "../hooks/use-create-campaign";
import { usePlacementCatalog } from "../hooks/use-placement-catalog";
import { useBrand } from "@/hooks/use-brand";
import { useCampaign } from "@/hooks/use-campaign";
import { ConfirmationDialog } from "@/components/campaign/dialogs/confirmation-dialog";

const steps = [
  "Brand",
  "Products",
  "Ad format",
  "Markets & merchants",
  "Placement",
  "Budget",
  "Review & launch",
] as const;

type NewCampaignPageProps = {
  onStepLabelChange: (label: string) => void;
};
type NewCampaignLocationState = {
  cloneCampaignId?: string;
};

export function NewCampaignPage({ onStepLabelChange }: NewCampaignPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as NewCampaignLocationState | null;

  const cloneCampaignId = state?.cloneCampaignId;
  const cloneCampaign = useCampaign(cloneCampaignId);
  const cloneBrandId = getCampaignBrandId(cloneCampaign.data);
  const cloneBrand = useBrand(cloneBrandId, {
    enabled: Boolean(cloneCampaignId && cloneBrandId),
  });
  const initializedCloneCampaignId = useRef<string | null>(null);

  const [currentStep, setCurrentStep] = useState<number>(
    CampaignCreationSteps.BRAND,
  );

  const [stepState, setStepState] = useState<{
    canProceed: boolean;
    errorMessage?: string;
    errorField?: string;
  }>({
    canProceed: false,
  });
  const [showStepError, setShowStepError] = useState(false);
  const createCampaign = useCreateCampaign();
  const { data: channels = [] } = usePlacementCatalog();
  const formik = useFormik<CampaignFormValues>({
    initialValues: defaultCampaignFormValues,
    onSubmit: async (values) => {
      await createCampaign.mutateAsync({ values, channels });
      formik.resetForm({ values });
      navigate("/campaigns");
    },
  });

  const totalSteps = steps.length;

  useEffect(() => {
    onStepLabelChange(steps[currentStep - 1]);
  }, [currentStep, onStepLabelChange]);

  useEffect(() => {
    if (
      !cloneCampaignId ||
      !cloneCampaign.data ||
      initializedCloneCampaignId.current === cloneCampaignId
    ) {
      return;
    }

    formik.resetForm({
      values: getCloneCampaignFormValues(cloneCampaign.data, null),
    });
    initializedCloneCampaignId.current = cloneCampaignId;
  }, [cloneCampaign.data, cloneCampaignId, formik]);

  useEffect(() => {
    if (
      !cloneCampaignId ||
      initializedCloneCampaignId.current !== cloneCampaignId ||
      !cloneBrandId ||
      !cloneBrand.data ||
      formik.values.brandId !== cloneBrandId ||
      formik.values.brand?.id === cloneBrand.data.id
    ) {
      return;
    }

    void formik.setFieldValue("brand", cloneBrand.data, false);
  }, [cloneBrand.data, cloneBrandId, cloneCampaignId, formik]);

  const updateStepState = useCallback(
    (state: {
      canProceed: boolean;
      errorMessage?: string;
      errorField?: string;
    }) => {
      setStepState(state);

      if (state.canProceed) {
        setShowStepError(false);
      }
    },
    [],
  );

  const nextStep = () => {
    if (!stepState.canProceed) {
      setShowStepError(true);
      return;
    }

    setShowStepError(false);
    setCurrentStep((step) => Math.min(step + 1, totalSteps));
  };

  const handleNext = () => {
    if (currentStep === CampaignCreationSteps.REVIEW_AND_LAUNCH) {
      void formik.submitForm();
      return;
    }

    nextStep();
  };

  const goToStep = (step: number) => {
    setShowStepError(false);
    setCurrentStep(step);
  };

  const previousStep = () => {
    setShowStepError(false);
    setCurrentStep((step) => Math.max(step - 1, CampaignCreationSteps.BRAND));
  };

  const nextLabel =
    createCampaign.isPending && currentStep === totalSteps
      ? "Launching..."
      : currentStep < totalSteps
        ? `${steps[currentStep]}`
        : "Launch campaign";

  const submitErrorMessage = createCampaign.error
    ? createCampaign.error instanceof Error
      ? createCampaign.error.message
      : "Unable to launch campaign."
    : undefined;
  const showLoadingSelectedBrand = Boolean(
    cloneCampaignId && cloneCampaign.isLoading,
  );

  const shouldBlock =
    formik.dirty && !formik.isSubmitting && !createCampaign.isPending;

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return shouldBlock && currentLocation.pathname !== nextLocation.pathname;
  });

  useEffect(() => {
    if (!shouldBlock) return;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [shouldBlock]);

  return (
    <FormikProvider value={formik}>
      <main className="tw:flex tw:min-w-0 tw:flex-col tw:max-w-7xl">
        <div className="tw:flex tw:min-w-0">
          <ProgressSidebar selectedStep={currentStep} onStepSelect={goToStep} />
          <div className="tw:min-w-0 tw:flex-1">
            <div className="tw:px-6 tw:pt-8 tw:pb-0 sm:tw:px-8">
              <StepRenderer
                steps={steps}
                currentStep={currentStep}
                setStepState={updateStepState}
                showError={showStepError}
                errorMessage={stepState.errorMessage}
                errorField={stepState.errorField}
                onDismissError={() => setShowStepError(false)}
                showLoadingSelectedBrand={showLoadingSelectedBrand}
                isSubmitting={createCampaign.isPending}
                submitErrorMessage={submitErrorMessage}
              />
            </div>
            <CampaignFooter
              currentStep={currentStep}
              totalSteps={totalSteps}
              nextLabel={nextLabel}
              canProceed={stepState.canProceed}
              isSubmitting={createCampaign.isPending}
              onBack={previousStep}
              onNext={handleNext}
            />
          </div>
        </div>
      </main>
      <ConfirmationDialog
        open={blocker.state === "blocked"}
        onOpenChange={(open) => {
          if (!open && blocker.state === "blocked") blocker.reset();
        }}
        title="Leave campaign setup?"
        description="Your progress will be lost if you leave this page."
        confirmLabel="Leave"
        confirmVariant="destructive"
        onConfirm={() => blocker?.proceed?.()}
      />
    </FormikProvider>
  );
}
